import { useEffect, useMemo, useState, useCallback } from 'react';
import type { TemplateBuilderTemplate, TemplateBuilderForm } from '../../types/templateTypes';
import { buildRuleLineInventory, type RuleLineInfo } from './ruleLinesInventory';

// ---------------------------------------------------------------------------
// RuleConnectionLines — SVG overlay that draws rectilinear Manhattan-routed
// paths between form blocks connected by template-level rules. Each
// cross-form rule is assigned a unique vertical lane in a right-side gutter
// and tagged with a numbered badge; the numbering matches RuleLinesLegend.
// Self-form rules are not drawn here (they are surfaced on the form card
// via a ↻ internal-rules chip).
// ---------------------------------------------------------------------------

export interface RuleConnectionLinesProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
  /** Map from formId → DOM element (the form block card). */
  formBlockRefs: Map<string, HTMLElement>;
  /** Container element that is the SVG's positioning parent. */
  containerRef: React.RefObject<HTMLElement>;
  /** Whether to show the overlay. */
  visible: boolean;
  /** Ordered list of visible form IDs — used as a stable effect dependency. */
  formIds: string[];
  /** Currently hovered rule (lifted to TemplateCanvas for legend sync). */
  hoveredRuleId: string | null;
  /** Notify parent of hover changes so the legend panel can highlight. */
  onHoverRule: (ruleId: string | null) => void;
}

interface LaidOutLine extends RuleLineInfo {
  sx: number; sy: number;   // source anchor (form card right edge)
  tx: number; ty: number;   // target anchor (form card right edge)
  laneX: number;            // x of the vertical gutter lane for this line
}

const LANE_GUTTER_START = 36;   // px right of source/target x where lanes begin
const LANE_SPACING = 14;        // px between lanes
const BADGE_RADIUS = 9;

function layoutLines(
  lines: RuleLineInfo[],
  formBlockRefs: Map<string, HTMLElement>,
  container: HTMLElement,
): LaidOutLine[] {
  const containerRect = container.getBoundingClientRect();
  let maxRight = 0;
  const anchors = new Map<string, { x: number; y: number }>();
  for (const [fid, el] of formBlockRefs) {
    const r = el.getBoundingClientRect();
    anchors.set(fid, {
      x: r.right - containerRect.left,
      y: r.top + r.height / 2 - containerRect.top,
    });
    if (r.right - containerRect.left > maxRight) maxRight = r.right - containerRect.left;
  }
  const laid: LaidOutLine[] = [];
  lines.forEach((line, idx) => {
    const src = anchors.get(line.sourceFormId);
    const tgt = anchors.get(line.targetFormId);
    if (!src || !tgt) return;
    const laneX = maxRight + LANE_GUTTER_START + idx * LANE_SPACING;
    laid.push({ ...line, sx: src.x, sy: src.y, tx: tgt.x, ty: tgt.y, laneX });
  });
  return laid;
}

export function RuleConnectionLines({
  template,
  forms,
  formBlockRefs,
  containerRef,
  visible,
  formIds,
  hoveredRuleId,
  onHoverRule,
}: RuleConnectionLinesProps) {
  const inventory = useMemo(() => buildRuleLineInventory(template, forms), [template, forms]);
  const [laidLines, setLaidLines] = useState<LaidOutLine[]>([]);
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 });

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container || !visible) { setLaidLines([]); return; }
    const rect = container.getBoundingClientRect();
    // SVG width must cover the gutter — extend beyond the scroll viewport if needed
    const laid = layoutLines(inventory, formBlockRefs, container);
    const maxLaneX = laid.reduce((m, l) => Math.max(m, l.laneX + BADGE_RADIUS + 4), 0);
    setSvgSize({ w: Math.max(rect.width, maxLaneX), h: rect.height });
    setLaidLines(laid);
  }, [inventory, formBlockRefs, containerRef, visible]);

  useEffect(() => {
    recalculate();
    const obs = new ResizeObserver(recalculate);
    if (containerRef.current) obs.observe(containerRef.current);
    window.addEventListener('scroll', recalculate, true);
    return () => {
      obs.disconnect();
      window.removeEventListener('scroll', recalculate, true);
    };
    // formIds intentionally in deps for effect stability when form ordering changes
  }, [recalculate, formIds]);

  if (!visible || laidLines.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={svgSize.w}
      height={svgSize.h}
      style={{ zIndex: 10 }}
    >
      <defs>
        <marker id="arrow-hide" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
        </marker>
        <marker id="arrow-show" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
        </marker>
      </defs>

      {laidLines.map((line) => {
        // Rectilinear path: source.right → H laneX → V targetY → H targetX
        const d = `M ${line.sx},${line.sy} H ${line.laneX} V ${line.ty} H ${line.tx}`;
        const isHovered = hoveredRuleId === line.ruleId;
        const markerId = line.isHide ? 'arrow-hide' : 'arrow-show';
        const stroke = line.color;
        const badgeY = (line.sy + line.ty) / 2;

        return (
          <g key={`${line.ruleId}-${line.targetFormId}-${line.index}`}>
            {/* Invisible wider hit area for hover */}
            <path
              d={d}
              fill="none"
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke', cursor: 'default' }}
              onMouseEnter={() => onHoverRule(line.ruleId)}
              onMouseLeave={() => onHoverRule(null)}
            />
            {/* Visible rectilinear line */}
            <path
              d={d}
              fill="none"
              stroke={stroke}
              strokeWidth={isHovered ? 2.5 : 1.5}
              strokeDasharray={line.isHide ? '5,3' : undefined}
              markerEnd={`url(#${markerId})`}
              opacity={isHovered ? 1 : 0.5}
              style={{ transition: 'stroke-width 0.15s, opacity 0.15s' }}
            />
            {/* Numbered badge on the lane column */}
            <g
              style={{ pointerEvents: 'auto', cursor: 'default' }}
              onMouseEnter={() => onHoverRule(line.ruleId)}
              onMouseLeave={() => onHoverRule(null)}
            >
              <circle
                cx={line.laneX}
                cy={badgeY}
                r={BADGE_RADIUS}
                fill={stroke}
                opacity={isHovered ? 1 : 0.85}
                stroke="#ffffff"
                strokeWidth={1.5}
              />
              <text
                x={line.laneX}
                y={badgeY + 3}
                textAnchor="middle"
                fontSize={10}
                fontWeight={600}
                fill="#ffffff"
              >
                {line.index}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

export default RuleConnectionLines;
